import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSearch, FiFilter, FiStar, FiBookOpen, FiShoppingCart, FiChevronLeft, FiChevronRight, FiVideo, FiBook, FiTarget, FiX, FiPlay, FiPause } from 'react-icons/fi';
import { FaAmazon, FaExclamationTriangle } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SearchField from '../components/SearchField';
import { apiUrls } from '../config/api';
import { cartService } from '../services/cartService';

// Real services for dynamic filtering
const bookService = {
  getBooks: async (filters?: any) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.search) queryParams.append('search', filters.search);
      if (filters?.grade) queryParams.append('grade', filters.grade);
      if (filters?.country) queryParams.append('country', filters.country);
      if (filters?.state) queryParams.append('state', filters.state);
      
      const response = await fetch(`${apiUrls.books.list}?${queryParams}`);
      const data = await response.json();
      return { success: response.ok, data: data.books || [] };
    } catch (error) {
      console.error('Error fetching books:', error);
      return { success: false, data: [] };
    }
  }
};

const locationService = {
  getCountries: async () => {
    try {
      const response = await fetch(`${apiUrls.countries.list}?active=true`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('🌍 Countries API response:', data);
      // Backend returns array directly, not wrapped in object
      return { success: true, data: Array.isArray(data) ? data : data.countries || [] };
    } catch (error) {
      console.error('Error fetching countries:', error);
      return { success: false, data: [] };
    }
  },
  getStates: async () => {
    try {
      // Note: This method is deprecated - use getStatesByCountry instead
      console.log('⚠️ getStates() called - should use getStatesByCountry()');
      return { success: true, data: [] };
    } catch (error) {
      console.error('Error fetching states:', error);
      return { success: false, data: [] };
    }
  },
  getStatesByCountry: async (countryId: string) => {
    try {
      const response = await fetch(apiUrls.countries.states(countryId));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log(`🏛️ States for country ${countryId}:`, data);
      // Backend returns array directly, not wrapped in object
      return { success: true, data: Array.isArray(data) ? data : data.states || [] };
    } catch (error) {
      console.error('Error fetching states by country:', error);
      return { success: false, data: [] };
    }
  }
};

const dataService = {
  getSubjects: async () => {
    try {
      const response = await fetch(apiUrls.books.subjects);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('📚 Subjects API response:', data);
      // Backend returns {subjects: [...]} format
      return { success: true, data: data.subjects || [] };
    } catch (error) {
      console.error('Error fetching subjects:', error);
      return { success: false, data: [] };
    }
  },
  getGrades: async () => {
    try {
      const response = await fetch(apiUrls.books.grades);
      const data = await response.json();
      return { success: response.ok, data: data.grades || [] };
    } catch (error) {
      console.error('Error fetching grades:', error);
      return { success: false, data: [] };
    }
  }
};

// TypeScript Interfaces
interface BooksPageProps {
  onHomeClick: () => void;
  onBooksClick: () => void;
  onBookDetailClick: (bookId: string) => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onFaqClick?: () => void;
  onFeedbackClick?: () => void;
  onLoginClick: () => void;
  onAdminClick: () => void;
  onMkAcademyOnlineClick?: () => void;
  onMkAcademyInPersonClick?: () => void;
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  user?: any;
  onLogout?: () => void;
}

interface Book {
  id: number;
  title: string;
  author: string;
  description: string;
  price: number;
  image: string;
  subject: string;
  grade: string;
  rating: number;
  reviews: number;
  amazon_link?: string;
  video_link?: string;
  vendor_id: number;
  referal_link?: string; // Link to Amazon or other external retailers
  other_link?: string; // Link to Barnes & Noble or other retailers
  vendorInfo?: {
    vendor_id: number;
    vendorlist: string; // Comma-separated vendors: "amazon,barnesandnoble,mathknots"
    active: number;
  };
}

interface Country {
  id: number;
  name: string;
}

interface State {
  id: number;
  name: string;
  country_id: number;
}

interface Subject {
  id: number;
  name: string;
}

interface Grade {
  id: number;
  name: string;
}

interface FilterState {
  search: string;
  grade: string;
  country: string;
  state: string;
}

// CSS Variables
const CSS_VARS = {
  primaryColor: '#253C8C',
  secondaryColor: '#F9D51D',
  accentColor: '#E8F0FE',
  textPrimary: '#2D3748',
  textSecondary: '#4A5568',
  textLight: '#718096',
  bgPrimary: '#FFFFFF',
  bgSecondary: '#F7FAFC',
  bgLight: '#f8f9ff',
  borderLight: '#E2E8F0',
  shadowLight: '0 4px 20px rgba(37, 60, 140, 0.1)',
  shadowMedium: '0 6px 25px rgba(37, 60, 140, 0.15)',
  shadowHover: '0 8px 30px rgba(37, 60, 140, 0.2)',
  gradientPrimary: 'linear-gradient(135deg, #253C8C 0%, #1e3a8a 100%)',
  gradientSecondary: 'linear-gradient(135deg, #F9D51D 0%, #f59e0b 100%)',
  borderRadius: '12px',
  borderRadiusLarge: '20px',
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px'
  }
};

// Styled Components
const BooksSection = styled.section`
  padding-top: 140px;
  min-height: 100vh;
  background: linear-gradient(135deg, ${CSS_VARS.bgLight} 0%, #ffffff 100%);
`;

const BooksContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  padding-top: 10px;
`;

const BooksHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const BooksTitle = styled.h1`
  font-size: 3rem;
  color: ${CSS_VARS.primaryColor};
  margin-bottom: 15px;
  font-weight: 700;
  background: ${CSS_VARS.gradientPrimary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const BooksSubtitle = styled.p`
  font-size: 1.2rem;
  color: ${CSS_VARS.textLight};
  margin-bottom: 30px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const FilterMain = styled.div`
  background: white;
  border-radius: ${CSS_VARS.borderRadiusLarge};
  padding: 20px;
  margin-bottom: 15px;
  box-shadow: ${CSS_VARS.shadowLight};
  border: 1px solid ${CSS_VARS.borderLight};
`;

const FilterTitle = styled.h3`
  font-size: 1.3rem;
  color: ${CSS_VARS.primaryColor};
  margin-bottom: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  align-items: end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilterLabel = styled.label`
  font-size: 0.95rem;
  color: ${CSS_VARS.textSecondary};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FormSelect = styled.select`
  padding: 10px 12px;
  border: 2px solid ${CSS_VARS.borderLight};
  border-radius: ${CSS_VARS.borderRadius};
  font-size: 0.9rem;
  color: ${CSS_VARS.textPrimary};
  background: white;
  transition: all 0.3s ease;
  cursor: pointer;
  height: 44px;

  &:focus {
    outline: none;
    border-color: ${CSS_VARS.primaryColor};
    box-shadow: 0 0 0 3px rgba(37, 60, 140, 0.1);
  }

  &:hover {
    border-color: ${CSS_VARS.primaryColor};
  }
`;

const FilterActions = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 20px;
`;

const FilterButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: ${CSS_VARS.borderRadius};
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;
  justify-content: center;

  ${props => props.variant === 'primary' ? `
    background: ${CSS_VARS.gradientPrimary};
    color: white;
    box-shadow: ${CSS_VARS.shadowLight};

    &:hover {
      transform: translateY(-2px);
      box-shadow: ${CSS_VARS.shadowMedium};
    }
  ` : `
    background: white;
    color: ${CSS_VARS.primaryColor};
    border: 2px solid ${CSS_VARS.primaryColor};

    &:hover {
      background: ${CSS_VARS.primaryColor};
      color: white;
      transform: translateY(-2px);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const BooksPageFixed: React.FC<BooksPageProps> = ({ 
  onHomeClick, 
  onBooksClick, 
  onBookDetailClick,
  onVideoLearningClick, 
  onBlogsClick, 
  onFaqClick,
  onFeedbackClick,
  onLoginClick, 
  onAdminClick,
  onMkAcademyOnlineClick,
  onMkAcademyInPersonClick,
  isAuthenticated,
  isAdmin,
  user,
  onLogout
}) => {
  // Debug: Log what props BooksPage is receiving
  console.log('📚 BooksPage props received:', {
    onMkAcademyOnlineClick: onMkAcademyOnlineClick,
    onMkAcademyInPersonClick: onMkAcademyInPersonClick,
    hasOnMkAcademyOnlineClick: typeof onMkAcademyOnlineClick,
    hasOnMkAcademyInPersonClick: typeof onMkAcademyInPersonClick
  });

  // Filter states
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    grade: '',
    country: '230', // Default to United States (ID: 230)
    state: ''
  });

  // Data states
  const [books, setBooks] = useState<Book[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);

  // UI states
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [currentBookIndex, setCurrentBookIndex] = useState<number>(0);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<number>>(new Set());

  // Add carousel state and controls  
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const [isVideoInteracting, setIsVideoInteracting] = useState(false);
  const booksPerPage = 1; // Show only one book at a time
  
  // Get books for current page (one book per slide)
  const startIndex = currentSlide;
  const visibleBooks = books.slice(startIndex, startIndex + 1);
  const totalSlides = Math.min(books.length, books.length); // One slide per book



  // Carousel navigation functions
  const nextSlide = () => {
    setCurrentSlide(prev => Math.min(prev + 1, books.length - 1));
  };

  const prevSlide = () => {
    setCurrentSlide(prev => Math.max(prev - 1, 0));
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Video interaction handlers
  const [videoInteractionTimer, setVideoInteractionTimer] = useState<NodeJS.Timeout | null>(null);

  const handleVideoInteraction = () => {
    console.log('🎬 Video interaction detected - pausing carousel');
    setIsVideoInteracting(true);
    setIsCarouselPaused(true);
    
    // Clear any existing timer
    if (videoInteractionTimer) {
      clearTimeout(videoInteractionTimer);
    }
  };

  const handleVideoInteractionEnd = () => {
    console.log('🎬 Video interaction ended - resuming carousel in 3 minutes');
    // Clear any existing timer
    if (videoInteractionTimer) {
      clearTimeout(videoInteractionTimer);
    }
    
    // Resume carousel after 3 minutes of no interaction
    const timer = setTimeout(() => {
      console.log('🎬 Resuming carousel after video interaction timeout');
      setIsVideoInteracting(false);
      setIsCarouselPaused(false);
    }, 180000); // 3 minutes = 180,000 milliseconds
    
    setVideoInteractionTimer(timer);
  };

  // Reset video interaction state when slide changes
  useEffect(() => {
    // Clear any existing timer
    if (videoInteractionTimer) {
      clearTimeout(videoInteractionTimer);
      setVideoInteractionTimer(null);
    }
    setIsVideoInteracting(false);
    setIsCarouselPaused(false);
  }, [currentSlide, videoInteractionTimer]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (videoInteractionTimer) {
        clearTimeout(videoInteractionTimer);
      }
    };
  }, [videoInteractionTimer]);

  // Reset carousel when books change
  useEffect(() => {
    setCurrentSlide(0);
  }, [books]);

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Check if the user is interacting with video (common video control keys)
      if (event.key === ' ' || event.key === 'Space' || 
          event.key === 'ArrowUp' || event.key === 'ArrowDown' ||
          event.key === 'Enter' || event.key === 'Escape') {
        // These keys are commonly used for video control, pause carousel
        handleVideoInteraction();
        return;
      }
      
      if (event.key === 'ArrowLeft') {
        prevSlide();
      } else if (event.key === 'ArrowRight') {
        nextSlide();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlide, books.length]);

  // Auto-scroll carousel
  useEffect(() => {
    if (books.length <= 1 || isCarouselPaused || isVideoInteracting) return; // Don't auto-scroll if only one book, no books, paused, or video is being interacted with
    
    const interval = setInterval(() => {
      setCurrentSlide(prev => {
        // Loop back to start when reaching the end
        return prev >= books.length - 1 ? 0 : prev + 1;
      });
    }, 5000); // Auto-scroll every 5 seconds

    return () => clearInterval(interval);
  }, [books.length, isCarouselPaused, isVideoInteracting]);

  useEffect(() => {
    const initializeData = async () => {
      console.log('🚀 Initializing BooksPage data...');
      
      // Fetch all data first
      await Promise.all([
        fetchBooks(),
        fetchLocations(),
        fetchSubjectsAndGrades()
      ]);
      
      // Load states for United States by default after countries are loaded
      console.log('🇺🇸 Loading US states by default...');
      await handleCountryChange('230');
    };
    
    initializeData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters]);

  const fetchBooks = async (filters?: FilterState): Promise<void> => {
    try {
      setLoading(true);
      const response = await bookService.getBooks(filters);
      if (response.success) {
        setBooks(response.data);
        setError('');
      } else {
        setError('Failed to fetch books');
        setBooks([]);
      }
    } catch (err) {
      console.error('Error fetching books:', err);
      setError('Failed to fetch books');
      setBooks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async (): Promise<void> => {
    try {
      // Only fetch countries initially, states will be fetched when country is selected
      const countriesResponse = await locationService.getCountries();
      
      if (countriesResponse.success) {
        setCountries(countriesResponse.data || []);
        console.log('✅ Countries loaded successfully:', countriesResponse.data.length);
      } else {
        console.error('Failed to fetch countries');
        setCountries([]);
      }
      
      // Don't fetch all states initially - they will be loaded when country is selected
      setStates([]);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const fetchSubjectsAndGrades = async (): Promise<void> => {
    try {
      const [subjectsResponse, gradesResponse] = await Promise.all([
        dataService.getSubjects(),
        dataService.getGrades()
      ]);
      setSubjects(subjectsResponse.data || []);
      setGrades(gradesResponse.data || []);
    } catch (err) {
      console.error('Error fetching subjects and grades:', err);
    }
  };

  const clearFilters = async (): Promise<void> => {
    const newFilters = {
      search: filters.search, // Keep existing search
      grade: '',
      country: '230', // Reset to United States
      state: ''
    };
    setFilters(newFilters);
    setStates([]);
    
    // Fetch US states and apply filters
    try {
      const response = await locationService.getStatesByCountry('230');
      if (response.success) {
        setStates(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching US states:', err);
    }
    
    await fetchBooks(newFilters);
    setCurrentBookIndex(0);
  };

  const handleCountryChange = async (countryId: string): Promise<void> => {
    const newFilters = { ...filters, country: countryId, state: '' };
    setFilters(newFilters);
    
    if (countryId) {
      try {
        const response = await locationService.getStatesByCountry(countryId);
        if (response.success) {
          setStates(response.data || []);
        } else {
          console.error('Failed to fetch states for country:', countryId);
          setStates([]);
        }
      } catch (err) {
        console.error('Error fetching states:', err);
        setStates([]);
      }
    } else {
      setStates([]);
    }
    
    // Apply the new filters
    await fetchBooks(newFilters);
    setCurrentBookIndex(0);
  };

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const toggleDescription = (bookId: number): void => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bookId)) {
        newSet.delete(bookId);
      } else {
        newSet.add(bookId);
      }
      return newSet;
    });
  };

  const isDescriptionLong = (text: string): boolean => {
    return text.length > 150;
  };

  // Function to render purchase buttons based on vendor list
  const renderPurchaseButtons = (book: Book) => {
    // Based on your database: vendor_id=2 has vendorlist="amazon"
    // We'll create a simple mapping for now until the association is working
    const getVendorList = (vendorId: number): string => {
      switch (vendorId) {
        case 1:
          return 'mathknots';
        case 2:
          return 'amazon';
        case 3:
          return 'barnesandnoble';
        case 4:
          return 'amazon,barnesandnoble';
        case 5:
          return 'mathknots,amazon';
        default:
          return 'mathknots'; // Default fallback
      }
    };

    const vendorList = book.vendorInfo?.vendorlist || getVendorList(book.vendor_id);
    const vendors = vendorList.toLowerCase().split(',').map((v: string) => v.trim());
    const buttons = [];

    // Check for MathKnots
    if (vendors.includes('mathknots')) {
      buttons.push(
        <button key="mathknots" style={{
          flex: '1',
          padding: '12px 16px',
          background: CSS_VARS.primaryColor,
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          transition: 'all 0.3s ease',
          height: '48px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#1e40af';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = CSS_VARS.primaryColor;
          e.currentTarget.style.transform = 'translateY(0)';
        }}
        onClick={(e) => {
          e.preventDefault();
          // Add to cart functionality
          const cartItem = {
            id: book.id,
            title: book.title,
            price: Number(book.price) || 0,
            image: book.image,
            type: 'book' as const
          };
          
          const success = cartService.addToCart(cartItem);
          
          if (success) {
            // Auto-open cart
            window.dispatchEvent(new CustomEvent('openCart'));
          } else {
            alert('Failed to add to cart. Please try again.');
          }
        }}>
          <FiShoppingCart size={16} />
          Add to Cart
        </button>
      );
    }

    // Check for Amazon
    if (vendors.includes('amazon')) {
      buttons.push(
        <button key="amazon" style={{
          flex: '1',
          padding: '12px 16px',
          background: '#ff9500',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          transition: 'all 0.3s ease',
          height: '48px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#e68900';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#ff9500';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
        onClick={(e) => {
          e.preventDefault();
          if (book.referal_link) {
            window.open(book.referal_link, '_blank');
          } else {
            // Fallback Amazon link
            const amazonSearchUrl = `https://www.amazon.com/s?k=${encodeURIComponent(book.title)}`;
            window.open(amazonSearchUrl, '_blank');
          }
        }}>
          <FaAmazon size={16} />
          Buy on Amazon
        </button>
      );
    }

    // Check for Barnes & Noble
    if (vendors.includes('barnesandnoble') || vendors.includes('barnes&noble') || vendors.includes('bn')) {
      buttons.push(
        <button key="bn" style={{
          flex: '1',
          padding: '12px 16px',
          background: '#00704a',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          transition: 'all 0.3s ease',
          height: '48px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#005a3a';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#00704a';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
        onClick={(e) => {
          e.preventDefault();
          if (book.other_link) {
            window.open(book.other_link, '_blank');
          } else {
            // Fallback Barnes & Noble link
            const bnSearchUrl = `https://www.barnesandnoble.com/s/${encodeURIComponent(book.title)}`;
            window.open(bnSearchUrl, '_blank');
          }
        }}>
          <FiBook size={16} />
          Buy on B&N
        </button>
      );
    }

    return buttons.length > 0 ? buttons : (
      <button style={{
        flex: '1',
        padding: '12px 16px',
        background: CSS_VARS.primaryColor,
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        transition: 'all 0.3s ease',
        height: '48px'
      }}>
        <FiShoppingCart size={16} />
        Add to Cart
      </button>
    );
  };

  const applyFilters = async (): Promise<void> => {
    // Fetch books with current filters from the backend
    await fetchBooks(filters);
    setCurrentBookIndex(0);
  };

  const handleFilterChange = async (field: keyof FilterState, value: string): Promise<void> => {
    const newFilters = { ...filters, [field]: value };
    
    // If country is changed, update states and reset state filter
    if (field === 'country') {
      newFilters.state = '';
      setFilters(newFilters);
      
      // Fetch states for the new country
      if (value) {
        try {
          const response = await locationService.getStatesByCountry(value);
          if (response.success) {
            setStates(response.data || []);
          } else {
            console.error('Failed to fetch states for country:', value);
            setStates([]);
          }
        } catch (err) {
          console.error('Error fetching states:', err);
          setStates([]);
        }
      } else {
        setStates([]);
      }
    } else {
      // For state and grade changes, just update the filters
      setFilters(newFilters);
    }
    
    // Automatically apply filters with the new values
    await fetchBooks(newFilters);
    setCurrentBookIndex(0);
  };

  if (loading) {
    return (
      <BooksSection>
        <Header 
          onHomeClick={onHomeClick} 
          onBooksClick={onBooksClick} 
          onVideoLearningClick={onVideoLearningClick} 
          onBlogsClick={onBlogsClick} 
          onLoginClick={onLoginClick} 
          onAdminClick={onAdminClick}
          onMkAcademyOnlineClick={onMkAcademyOnlineClick}
          onMkAcademyInPersonClick={onMkAcademyInPersonClick}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          user={user}
          onLogout={onLogout}
        />
        <BooksContainer>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <div style={{ fontSize: '24px', color: CSS_VARS.primaryColor }}>Loading books...</div>
          </div>
        </BooksContainer>
      </BooksSection>
    );
  }

  if (error) {
    return (
      <BooksSection>
        <Header 
          onHomeClick={onHomeClick} 
          onBooksClick={onBooksClick} 
          onVideoLearningClick={onVideoLearningClick} 
          onBlogsClick={onBlogsClick} 
          onLoginClick={onLoginClick} 
          onAdminClick={onAdminClick}
          onMkAcademyOnlineClick={onMkAcademyOnlineClick}
          onMkAcademyInPersonClick={onMkAcademyInPersonClick}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          user={user}
          onLogout={onLogout}
        />
        <BooksContainer>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <FaExclamationTriangle size={48} color={CSS_VARS.primaryColor} style={{ marginBottom: '20px' }} />
            <div style={{ fontSize: '24px', color: CSS_VARS.primaryColor, marginBottom: '10px' }}>Error</div>
            <div style={{ color: CSS_VARS.textLight }}>{error}</div>
            <FilterButton variant="primary" onClick={() => fetchBooks()} style={{ marginTop: '20px' }}>
              Try Again
            </FilterButton>
          </div>
        </BooksContainer>
      </BooksSection>
    );
  }

  return (
    <>
    <BooksSection>
      <Header 
        onHomeClick={onHomeClick} 
        onBooksClick={onBooksClick} 
        onVideoLearningClick={onVideoLearningClick} 
        onBlogsClick={onBlogsClick} 
        onLoginClick={onLoginClick} 
        onAdminClick={onAdminClick}
        onMkAcademyOnlineClick={onMkAcademyOnlineClick}
        onMkAcademyInPersonClick={onMkAcademyInPersonClick}
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        user={user}
        onLogout={onLogout}
        currentPage="books"
      />
      <BooksContainer>
        {/* Filter Section */}
        <FilterMain>
          <FilterTitle>
            <FiFilter />
            Filter Books
          </FilterTitle>
          
          <FilterGrid>
            <FilterGroup>
              <FilterLabel>
                <FiSearch />
                Search Books
              </FilterLabel>
              <SearchField
                value={filters.search}
                onChange={(value) => handleFilterChange('search', value)}
                placeholder="Search by title, description... (case-insensitive)"
                isLoading={loading}
              />
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>
                <FiBookOpen />
                Country
              </FilterLabel>
              <FormSelect
                value={filters.country}
                onChange={(e) => handleFilterChange('country', e.target.value)}
              >
                {countries.map(country => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </FormSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>
                <FiTarget />
                State
              </FilterLabel>
              <FormSelect
                value={filters.state}
                onChange={(e) => handleFilterChange('state', e.target.value)}
                disabled={!filters.country}
              >
                <option value="">All States</option>
                {states.map(state => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </FormSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>
                <FiTarget />
                Grade
              </FilterLabel>
              <FormSelect
                value={filters.grade}
                onChange={(e) => handleFilterChange('grade', e.target.value)}
              >
                <option value="">All Grades</option>
                {grades.map(grade => (
                  <option key={grade.id} value={grade.id}>
                    {grade.name}
                  </option>
                ))}
              </FormSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel style={{ opacity: 0 }}>
                Actions
              </FilterLabel>
              <button
                onClick={() => clearFilters()}
                style={{
                  padding: '12px 20px',
                  background: 'transparent',
                  color: CSS_VARS.primaryColor,
                  border: `2px solid ${CSS_VARS.primaryColor}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  transition: 'all 0.3s ease',
                  height: '44px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = CSS_VARS.primaryColor;
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = CSS_VARS.primaryColor;
                }}
              >
                <FiX size={16} />
                Clear
              </button>
              

            </FilterGroup>
          </FilterGrid>
        </FilterMain>

        {/* Books Carousel */}
        {books.length > 0 ? (
          <div style={{ marginTop: '10px' }}>
            {/* Compact Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #253C8C 0%, #1e40af 100%)',
              borderRadius: '8px',
              padding: '10px 20px',
              marginBottom: '15px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              boxShadow: '0 4px 15px rgba(37, 60, 140, 0.15)'
            }}>
              <div>
                <h2 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white',
                  margin: 0
                }}>
                  📚 Our Books
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  margin: '2px 0 0 0',
                  fontWeight: '500'
                }}>
                  Book {currentSlide + 1} of {books.length} • {books.length} books available
                  {isVideoInteracting && (
                    <span style={{ 
                      marginLeft: '10px', 
                      fontSize: '12px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontWeight: '600'
                    }}>
                      ⏸️ Video Playing
                    </span>
                  )}
                </p>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  marginRight: '8px',
                  fontWeight: '600'
                }}>
                  {currentSlide + 1} / {books.length}
                </div>
                
                {/* Pause/Play Button */}
                <button
                  onClick={() => setIsCarouselPaused(!isCarouselPaused)}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    marginRight: '8px'
                  }}
                  title={isCarouselPaused ? 'Play carousel' : 'Pause carousel'}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  }}
                >
                  {isCarouselPaused ? <FiPlay size={14} /> : <FiPause size={14} />}
                </button>
                
                {/* Navigation Arrows */}
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    backgroundColor: currentSlide === 0 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                    color: currentSlide === 0 ? 'rgba(255, 255, 255, 0.4)' : 'white',
                    cursor: currentSlide === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    opacity: currentSlide === 0 ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (currentSlide !== 0) {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentSlide !== 0) {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                    }
                  }}
                >
                  <FiChevronLeft size={16} />
                </button>
                
                <button
                  onClick={nextSlide}
                  disabled={currentSlide === books.length - 1}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    backgroundColor: currentSlide === books.length - 1 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                    color: currentSlide === books.length - 1 ? 'rgba(255, 255, 255, 0.4)' : 'white',
                    cursor: currentSlide === books.length - 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    opacity: currentSlide === books.length - 1 ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (currentSlide !== books.length - 1) {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentSlide !== books.length - 1) {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                    }
                  }}
                >
                  <FiChevronRight size={16} />
                </button>
              </div>
            </div>

            {/* Single book display - Horizontal Layout */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '0 15px'
            }}>
              {visibleBooks.map((book) => (
                <div
                  key={book.id}
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '20px',
                    boxShadow: '0 6px 25px rgba(37, 60, 140, 0.12)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    maxWidth: '1100px',
                    width: '100%',
                    display: 'flex',
                    gap: '20px',
                    alignItems: 'stretch',
                    minHeight: '380px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 8px 35px rgba(37, 60, 140, 0.18)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 6px 25px rgba(37, 60, 140, 0.12)';
                  }}
                >
                  {/* Left: Book Image */}
                  <div style={{ 
                    flex: '0 0 220px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <img 
                      src={`/images/${book.image}`} 
                      alt={book.title}
                      style={{ 
                        width: '100%', 
                        height: '280px', 
                        objectFit: 'cover',
                        borderRadius: '10px',
                        marginBottom: '12px',
                        boxShadow: '0 3px 12px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    
                    {/* Price prominently displayed under image */}
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: '700', 
                      color: CSS_VARS.primaryColor,
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0ff 100%)',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: `2px solid ${CSS_VARS.primaryColor}`,
                      marginBottom: '8px'
                    }}>
                      {formatPrice(book.price)}
                    </div>
                  </div>

                  {/* Middle: Video Player */}
                  <div style={{ 
                    flex: '0 0 360px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isVideoInteracting 
                      ? 'linear-gradient(135deg, #e8f0fe 0%, #dbeafe 100%)' 
                      : 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)',
                    borderRadius: '12px',
                    padding: '15px',
                    border: isVideoInteracting 
                      ? '2px solid rgba(37, 60, 140, 0.3)' 
                      : '2px solid rgba(37, 60, 140, 0.1)',
                    transition: 'all 0.3s ease',
                    position: 'relative'
                  }}>
                    {book.video_link ? (
                      <div 
                        style={{ 
                          width: '100%', 
                          height: '240px',
                          position: 'relative'
                        }}
                        onMouseEnter={handleVideoInteraction}
                        onMouseLeave={handleVideoInteractionEnd}
                        onClick={handleVideoInteraction}
                        onFocus={handleVideoInteraction}
                        onBlur={handleVideoInteractionEnd}
                      >
                        <iframe
                          width="100%"
                          height="240"
                          src={book.video_link}
                          title={`${book.title} Video Preview`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allowFullScreen
                          style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '8px',
                            cursor: 'pointer'
                          }}
                          onLoad={() => {
                            console.log('🎬 Video iframe loaded');
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '240px',
                        background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#64748b',
                        textAlign: 'center'
                      }}>
                        <FiVideo size={40} style={{ marginBottom: '12px', opacity: 0.6 }} />
                        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '6px' }}>
                          Video Preview
                        </h4>
                        <p style={{ fontSize: '12px', opacity: 0.8 }}>
                          Video coming soon for this book
                        </p>
                      </div>
                    )}
                    
                    {/* Video action button */}
                    <button style={{
                      marginTop: '12px',
                      flex: '1',
                      padding: '12px 16px',
                      background: CSS_VARS.primaryColor,
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.3s ease',
                      height: '48px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#1e40af';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = CSS_VARS.primaryColor;
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      console.log('🎬 Play in full window button clicked!');
                      console.log('📖 Book video_link:', book.video_link);
                      console.log('⏸️ Carousel paused state before:', isCarouselPaused);
                      
                      if (book.video_link) {
                        // Pause carousel when video is opened
                        setIsCarouselPaused(true);
                        console.log('⏸️ Carousel paused!');
                        
                        // Open video in popup window
                        const videoWindow = window.open(
                          book.video_link, 
                          'videoPreview',
                          'width=800,height=600,scrollbars=yes,resizable=yes'
                        );
                        console.log('🪟 Video window opened:', videoWindow);
                        
                        // Monitor if tab is closed or video ends
                        const checkVideoStatus = setInterval(() => {
                          if (videoWindow && videoWindow.closed) {
                            // Tab was closed, resume carousel
                            console.log('🔄 Video tab closed, resuming carousel');
                            clearInterval(checkVideoStatus);
                            setIsCarouselPaused(false);
                          }
                        }, 1000);
                        
                        // Fallback: resume carousel after 10 minutes if tab is still open
                        setTimeout(() => {
                          console.log('⏰ 10-minute fallback triggered');
                          clearInterval(checkVideoStatus);
                          if (!videoWindow || !videoWindow.closed) {
                            setIsCarouselPaused(false);
                          }
                        }, 600000); // 10 minutes fallback
                      } else {
                        console.log('❌ No video_link available for this book');
                      }
                    }}
                    disabled={!book.video_link}
                    >
                      <FiPlay size={16} />
                      {book.video_link ? 'Play in full window' : 'Coming Soon'}
                    </button>
                  </div>

                  {/* Right: Book Details */}
                  <div style={{ 
                    flex: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    paddingLeft: '8px'
                  }}>
                    {/* Book Info */}
                    <div>
                      <h3 style={{ 
                        fontSize: '24px', 
                        fontWeight: '700', 
                        color: CSS_VARS.primaryColor,
                        marginBottom: '6px',
                        lineHeight: '1.2'
                      }}>
                        {book.title}
                      </h3>

                      <p style={{ 
                        color: CSS_VARS.textLight, 
                        fontSize: '16px',
                        marginBottom: '12px',
                        fontWeight: '500'
                      }}>
                        by {book.author}
                      </p>

                      {/* Rating */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        marginBottom: '15px'
                      }}>
                        {[...Array(5)].map((_, i) => (
                          <FiStar 
                            key={i} 
                            size={16} 
                            color={i < book.rating ? CSS_VARS.secondaryColor : '#e2e8f0'} 
                          />
                        ))}
                        <span style={{ fontSize: '14px', color: CSS_VARS.textLight, marginLeft: '6px' }}>
                          ({book.reviews} reviews)
                        </span>
                      </div>

                      {/* Description */}
                      <div style={{ 
                        fontSize: '14px', 
                        color: CSS_VARS.textPrimary,
                        lineHeight: '1.5',
                        marginBottom: '20px',
                        maxHeight: '100px',
                        overflowY: 'auto',
                        paddingRight: '8px'
                      }}>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: expandedDescriptions.has(book.id) 
                              ? book.description 
                              : isDescriptionLong(book.description)
                              ? `${book.description.substring(0, 200)}...`
                              : book.description
                          }}
                        />
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: '10px'
                    }}>
                      {/* Main Action Buttons Row */}
                      <div style={{ 
                        display: 'flex', 
                        gap: '10px',
                        flexWrap: 'wrap'
                      }}>
                        {/* Read More Button - only show if description is long */}
                        {isDescriptionLong(book.description) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('🔗 Opening book detail for book:', book);
                              console.log('📷 Book image on main page:', book.image);
                              console.log('🆔 Book ID being passed:', book.id);
                              // Open book detail in new tab
                              window.open(`${window.location.origin}?page=bookDetail&id=${book.id}`, '_blank');
                            }}
                            style={{
                              padding: '12px 16px',
                              background: '#f8fafc',
                              color: CSS_VARS.primaryColor,
                              border: `2px solid ${CSS_VARS.primaryColor}`,
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              transition: 'all 0.3s ease',
                              height: '48px',
                              minWidth: '120px',
                              flex: '0 0 auto'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = CSS_VARS.primaryColor;
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#f8fafc';
                              e.currentTarget.style.color = CSS_VARS.primaryColor;
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <FiBookOpen size={16} />
                            Read More
                          </button>
                        )}
                        
                        {/* Dynamic Purchase Buttons based on vendor_list */}
                        {renderPurchaseButtons(book)}
                      </div>

                      {/* External Links Row */}
                      {book.amazon_link && (
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <a 
                            href={book.amazon_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              padding: '12px 16px',
                              background: '#ff9900',
                              color: 'white',
                              textDecoration: 'none',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              transition: 'all 0.3s ease',
                              height: '48px',
                              minWidth: '200px'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#e68a00';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#ff9900';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <FaAmazon size={16} />
                            View on Amazon
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '50px',
            color: CSS_VARS.textLight
          }}>
            <FiBook size={48} style={{ marginBottom: '20px', opacity: 0.5 }} />
            <h3>No books found</h3>
            <p>Try adjusting your search criteria</p>
          </div>
        )}
      </BooksContainer>
      
    </BooksSection>
    <Footer 
      onBooksClick={onBooksClick}
      onVideoLearningClick={onVideoLearningClick}
      onBlogsClick={onBlogsClick}
      onFaqClick={onFaqClick}
      onFeedbackClick={onFeedbackClick}
      onMkAcademyOnlineClick={onMkAcademyOnlineClick}
    />
    </>
  );
};

export default BooksPageFixed; 